/**
 * システム設定: BB ID・内線番号の生成ルール(2026-09-09 ②・⑧指示、2026-09-20 D-112)。
 *   BB ID = 形態コード＋連番 / 内線番号 = その連番の数字
 *   形態コード(D-71) = 出勤形態(管理M／出勤A／在宅H／タイミーT)＋報酬区分(時給H／歩合C)＋架電内容(給湯器A／不用品B)
 *   例: 出勤×時給×給湯器 = AHA / 在宅×歩合×不用品 = HCB
 *   旧コード(SN／SR／RS／STM／SNM＋W)にも1クリックで戻せる。**既存のBB IDは変わらない**(自動採番は新規登録とCSVの空欄だけ)
 * ゆくゆくは API 連携で システム → BlueBean に自動反映する(Phase 5)。
 * 2026-09-20 UIデザインシステムへ移行(D-111)。
 * 2026-09-22 いちばん上に「外観設定(テーマ)」を追加(D-132)。?theme= で来たらそのテーマを選んだ状態で開く。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useCurrentUser } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import {
  BB_PREFIX_KEYS,
  BB_STYLE_KEYS,
  BB_STYLE_LABELS,
  BUSINESS_LABELS,
  DEFAULT_BB_NUMBERING,
  bbPrefixKey,
  bbTypeCode,
  type BbNumberingSettings,
  type BbPrefixKey,
  type BusinessType,
} from '@core/lib/types';
import ThemeSettingsPanel from '@core/theme/ThemeSettingsPanel';
import StaffSummaryColorsPanel from '@core/dashboard/StaffSummaryColorsPanel';
import { isThemeId } from '@core/theme/themes';
import { fetchBbNumbering, saveBbNumbering } from './bbNumbering';

type RuleText = { prefix: string; start: string; digits: string };

function toText(s: BbNumberingSettings): Record<BbPrefixKey, RuleText> {
  return Object.fromEntries(
    BB_PREFIX_KEYS.map((k) => [k, { prefix: s.rules[k].prefix, start: String(s.rules[k].start), digits: String(s.rules[k].digits) }]),
  ) as Record<BbPrefixKey, RuleText>;
}

const BUSINESSES = Object.keys(BUSINESS_LABELS) as BusinessType[];
/** 形態コード(D-71)を使った形態コード一覧 */
const CODE_PREFIXES = Object.fromEntries(BB_STYLE_KEYS.flatMap((s) => BUSINESSES.map((b) => [bbPrefixKey(s, b), bbTypeCode(s, b)]))) as Record<BbPrefixKey, string>;

export default function SettingsSystemPage() {
  const user = useCurrentUser();
  const toast = useToast();
  const [rules, setRules] = useState<Record<BbPrefixKey, RuleText>>(toText(DEFAULT_BB_NUMBERING));
  /** 保存済みの値(未保存の判定に使う) */
  const [saved, setSaved] = useState<Record<BbPrefixKey, RuleText>>(toText(DEFAULT_BB_NUMBERING));
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const dirty = JSON.stringify(rules) !== JSON.stringify(saved);
  /** 外観設定で選んだまま「適用する」を押していない */
  const [themeDirty, setThemeDirty] = useState(false);
  const [colorsDirty, setColorsDirty] = useState(false);
  const guard = useUnsavedGuard(dirty || themeDirty || colorsDirty);
  const [params] = useSearchParams();
  const themeParam = params.get('theme');
  const { hash } = useLocation();

  // デザイン比較の「このテーマを使う」から来たときは、外観設定の位置まで送る
  useEffect(() => {
    if (hash === '#appearance') document.getElementById('appearance')?.scrollIntoView({ block: 'start' });
  }, [hash]);

  useEffect(() => {
    void fetchBbNumbering()
      .then((s) => {
        setRules(toText(s));
        setSaved(toText(s));
      })
      .catch(() => toast('採番ルールの読み込みに失敗しました(既定値を表示しています)'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setRule(key: BbPrefixKey, patch: Partial<RuleText>) {
    setRules((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }

  /** 形態コードをまとめて入れ替える(保存するまでは画面の中だけ) */
  function applyPrefixes(of: (k: BbPrefixKey) => string) {
    setRules((prev) => Object.fromEntries(BB_PREFIX_KEYS.map((k) => [k, { ...prev[k], prefix: of(k) }])) as Record<BbPrefixKey, RuleText>);
  }
  const usingCode = BB_PREFIX_KEYS.every((k) => rules[k].prefix.trim().toUpperCase() === CODE_PREFIXES[k]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const settings: BbNumberingSettings = {
        rules: Object.fromEntries(
          BB_PREFIX_KEYS.map((k) => [k, { prefix: rules[k].prefix.trim(), start: Number(rules[k].start), digits: Number(rules[k].digits) }]),
        ) as BbNumberingSettings['rules'],
      };
      await saveBbNumbering(settings, user.id);
      setSaved(rules);
      toast('保存しました');
    } catch (err) {
      toast(err instanceof Error ? err.message : '保存に失敗しました');
    } finally {
      setBusy(false);
    }
  }

  const sample = (key: BbPrefixKey) => {
    const n = String(Number(rules[key].start) || 0).padStart(Number(rules[key].digits) || 1, '0');
    return { id: `${rules[key].prefix}${n}`, ext: n };
  };

  return (
    <div className="screen">
      {guard.modal}
      <PageHeader title="システム設定" description="画面のテーマ・日次【集計】の色と、BB ID・内線番号の作り方を決めます" />

      <ThemeSettingsPanel initial={isThemeId(themeParam) ? themeParam : null} onDirtyChange={setThemeDirty} />

      <StaffSummaryColorsPanel onDirtyChange={setColorsDirty} />

      <Panel
        title="BB ID・内線番号の生成ルール"
        note="BB ID＝形態コード＋連番、内線番号＝連番の数字。番号は「開始値」と「登録済みの最大＋1」の大きい方から続きます(タイミーは時給のみ)。**すでに登録されているBB IDは変わりません**。自動で採番するのは新規登録とCSVの空欄だけです"
        actions={
          !loading ? (
            <>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={usingCode} onClick={() => applyPrefixes((k) => CODE_PREFIXES[k])}>
                コード(AHA形式)を使う
              </button>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={() => applyPrefixes((k) => DEFAULT_BB_NUMBERING.rules[k].prefix)}>
                旧コード(SN・SR…)に戻す
              </button>
            </>
          ) : undefined
        }
      >
        {loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : (
          <form onSubmit={handleSubmit}>
            {/* 出勤形態の縦結合(rowSpan)があるため縦積みにせず、スマホでは表の中だけ横スクロール */}
            <div className="report-scroll is-auto is-matrix">
              <table className="report-table no-stack" style={{ maxWidth: 940 }}>
                <thead>
                  <tr>
                    <th scope="col" style={{ width: 130 }}>出勤形態×報酬区分</th>
                    <th scope="col" style={{ width: 86 }}>架電内容</th>
                    <th scope="col" className="is-center" style={{ width: 72 }} title="出勤形態(M/A/H/T)＋報酬区分(H/C)＋架電内容(A/B)">
                      コード
                    </th>
                    <th scope="col" className="is-center" style={{ width: 72 }} title="2026-09-20 まで使っていた形態コード">
                      旧コード
                    </th>
                    <th scope="col" style={{ width: 110 }}>形態コード</th>
                    <th scope="col" style={{ width: 110 }}>開始値</th>
                    <th scope="col" style={{ width: 84 }}>桁数</th>
                    <th scope="col">見本(BB ID ／ 内線)</th>
                  </tr>
                </thead>
                <tbody>
                  {BB_STYLE_KEYS.flatMap((s) =>
                    BUSINESSES.map((b, i) => {
                      const k = bbPrefixKey(s, b);
                      return (
                        <tr key={k}>
                          {i === 0 && (
                            <th scope="row" rowSpan={BUSINESSES.length} style={{ verticalAlign: 'top' }}>
                              {BB_STYLE_LABELS[s]}
                            </th>
                          )}
                          <td>{BUSINESS_LABELS[b]}</td>
                          <td className="is-center">
                            <code className="key-tag">{CODE_PREFIXES[k]}</code>
                          </td>
                          <td className="is-center ui-hint">{DEFAULT_BB_NUMBERING.rules[k].prefix}</td>
                          <td>
                            <input className="ui-input" aria-label={`${BB_STYLE_LABELS[s]}×${BUSINESS_LABELS[b]} の形態コード`} value={rules[k].prefix} onChange={(e) => setRule(k, { prefix: e.target.value })} style={{ width: 92 }} />
                          </td>
                          <td>
                            <input className="ui-input" inputMode="numeric" aria-label={`${BB_STYLE_LABELS[s]}×${BUSINESS_LABELS[b]} の開始値`} value={rules[k].start} onChange={(e) => setRule(k, { start: e.target.value })} style={{ width: 92 }} />
                          </td>
                          <td>
                            <input className="ui-input" inputMode="numeric" aria-label={`${BB_STYLE_LABELS[s]}×${BUSINESS_LABELS[b]} の桁数`} value={rules[k].digits} onChange={(e) => setRule(k, { digits: e.target.value })} style={{ width: 64 }} />
                          </td>
                          <td>
                            <b>{sample(k).id}</b> <span className="ui-hint">内線 {sample(k).ext}</span>
                          </td>
                        </tr>
                      );
                    }),
                  )}
                </tbody>
              </table>
            </div>
            <div className="report-toolbar report-toolbar-end" style={{ marginTop: 'var(--space-4)' }}>
              <button className="ui-btn ui-btn-primary" type="submit" disabled={busy || !dirty}>
                {busy ? '保存中…' : '保存する'}
              </button>
            </div>
          </form>
        )}
      </Panel>

      <Panel title="確認事項">
        <ul className="ui-hint" style={{ paddingLeft: 20, lineHeight: 1.9 }}>
          <li>
            コード(D-71)は <b>出勤形態 管理M／出勤A／在宅H／タイミーT</b> ＋ <b>報酬区分 時給H／歩合C</b> ＋ <b>架電内容 給湯器A／不用品B</b> の3文字です。架電内容が増えるときは C・D… と足します(型を1件足す作業が要るのでご連絡ください)
          </li>
          <li>形態コードを変えても、<b>すでに登録されているBB IDはそのまま</b>です。新しく登録する人と、CSVでBB IDが空欄の人だけが新しいコードになります</li>
          <li>BlueBean側へのID・パスワード登録は当面手作業。API連携(システム→BlueBean 自動反映)はソフツー回答後に実装</li>
        </ul>
      </Panel>
    </div>
  );
}
